import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Popover } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TertiaryButton from "../Buttons/TertiaryButton/TertiaryButton";
import GlobalSearchBar from "./GlobalSearchBar/GlobalSearchBar";
import GlobalSearchResults from "./GlobalSearchResults/GlobalSearchResults";
import { removeSpecialCharacters } from "../../utils/formatHelpers";
import { LoadService } from "../../services/LoadService";
import { CarrierService } from "../../services/CarrierService";
import { AlertContext } from "../Dashboard/Dashboard";

export default function GlobalSearch({ handleTableAction }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pathType, setPathType] = useState("load-profile");
  const [selectedSearchType, setSelectedSearchType] = useState("order-id");
  const [searchInput, setSearchInput] = useState("");
  const [parsedSearchInput, setParsedSearchInput] = useState("");
  const [resultsDisplayInput, setResultsDisplayInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchDisabled, setSearchDisabled] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [singleResult, setSingleResult] = useState(null);
  const open = Boolean(anchorEl);
  const pid = open ? "global-search-popover" : undefined;
  const minSearchCharacters = 3;

  const handleReset = () => {
    setSearchInput("");
    setParsedSearchInput("");
    setResultsDisplayInput("");
    setSearchResults([]);
    setSingleResult(null);
  };

  const handleSearchTypeChange = (value) => {
    setSelectedSearchType(value);
    handleReset();
  };

  const handleSearch = () => {
    if (!searchDisabled) {
      setSearchResults([]);
      setIsLoading(true);

      const params = {
        [selectedSearchType]: searchInput
      };

      switch (selectedSearchType) {
        case "order-id":
          LoadService.FindLoadsById(instance, searchInput).then(res => {
            if (res.length === 1 && selectedSearchType === "order-id") {
              setSingleResult(res[0].id);
              setAnchorEl(null);
            } else {
              setResultsDisplayInput(parsedSearchInput);
              setSearchResults(res);
            }
            setIsLoading(false);
          }).catch(error => {
            console.error(error);
            setIsLoading(false);
            warning(error);
          });
          break;
        case "carrierName":
        case "mcNumber":
        case "dotNumber":
          CarrierService.FindCarrier(instance, params).then(res => {
            if (res.length === 1) {
              setSingleResult(res[0].id);
              setAnchorEl(null);
            } else {
              setResultsDisplayInput(parsedSearchInput);
              setSearchResults(res);
            }
            setIsLoading(false);
          }).catch(error => {
            console.error(error);
            setIsLoading(false);
            warning(error);
          });
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    if (singleResult != null) {
      handleTableAction(pathType === "load-profile" ?
        { action: pathType, loadId: singleResult } :
        { action: pathType, carrierId: singleResult });
      handleReset();
    }
  }, [singleResult]);

  useEffect(() => {
    const parsedInput = removeSpecialCharacters(searchInput);
    setSearchDisabled(parsedInput.length < minSearchCharacters || isLoading);
    setParsedSearchInput(parsedInput);

    if (selectedSearchType === "order-id") {
      setPathType("load-profile");
    } else if (selectedSearchType === "carrierName" || selectedSearchType === "mcNumber" || selectedSearchType === "dotNumber") {
      setPathType("carrier-profile");
    }
  }, [searchInput, isLoading]);

  return (
    <>
      <TertiaryButton
        onClick={e => setAnchorEl(e.currentTarget)}
        startIcon={SearchIcon}
      />
      <Popover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        slotProps={{
          paper: {
            sx: { boxShadow: "elevation3" },
          }
        }}
      >
        <Box sx={{ minWidth: "1000px", maxWidth: "70vw", maxHeight: "70vh", padding: "20px 20px 20px 40px", overflow: "auto" }}>
          <GlobalSearchBar
            selectedSearchType={selectedSearchType}
            setSelectedSearchType={handleSearchTypeChange}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            handleSearch={handleSearch}
            handleClose={() => setAnchorEl(null)}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
          />
          <GlobalSearchResults
            isLoading={isLoading}
            selectedSearchType={selectedSearchType}
            searchResults={searchResults}
            searchInput={resultsDisplayInput}
            handleTableAction={handleTableAction}
          />
        </Box>
      </Popover>
    </>
  );
}

GlobalSearch.propTypes = {
  handleTableAction: PropTypes.func
};